// public/js/pages/tenants.js
import { loadTenants, createTenant, updateTenant, assignUserToTenant } from '../modules/tenants.js';
import { getCurrentUser, can } from '../modules/rbac.js';
import { setTenantAddon } from '../features/reports/api.js';
import { tName } from '../modules/demo-mode.js';

export async function initTenantsPage(container) {
  const currentUser = getCurrentUser();
  const groups = currentUser.groups || [];

  if (!can(groups, 'listTenants')) {
    container.innerHTML = `<div class="alert alert-danger">Access denied.</div>`;
    return;
  }

  // Granting the paid data-export add-on is a platform action (mirrors the
  // grantDevice boundary). Tenant admins see the tenant list but not this.
  const isPlatform = can(groups, 'grantDevice');
  const colspan = isPlatform ? 5 : 4;

  container.innerHTML = `
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2 class="h4 mb-0">Tenants <span class="badge bg-primary ms-2" id="tenant-count">0</span></h2>
      ${can(groups, 'createTenant') ? `<button id="btn-new-tenant" class="btn btn-success">
        <i class="fa-solid fa-plus me-2"></i> Create New Tenant
      </button>` : ''}
    </div>

    <div class="card shadow-sm">
      <div class="card-body p-0 table-responsive">
        <table class="table table-hover mb-0">
          <thead class="table-light">
            <tr>
              <th>Name</th>
              <th>Contact Person</th>
              <th>Contact Email</th>
              <th>Created</th>
              ${isPlatform ? '<th>Add-ons</th>' : ''}
            </tr>
          </thead>
          <tbody id="tenants-tbody"></tbody>
        </table>
      </div>
    </div>

    ${tenantModalHTML('create')}
    ${tenantModalHTML('edit')}
    ${addonModalHTML()}
  `;

  const tbody = document.getElementById('tenants-tbody');
  const countBadge = document.getElementById('tenant-count');

  // ── Render ──────────────────────────────────────────────────────────────
  async function renderTenants() {
    window.showPlaneSpinner(null, 'Loading tenants...');
    try {
      const tenants = await loadTenants();
      countBadge.textContent = tenants.length;

      tbody.innerHTML = tenants.length === 0
        ? `<tr><td colspan="${colspan}" class="text-center text-muted py-4">No tenants yet.</td></tr>`
        : tenants.map(t => `
            <tr data-tenant-id="${t.tenantId}">
              <td>
                <span
                  class="tenant-name-link text-primary fw-semibold"
                  style="cursor:pointer;"
                  title="UUID: ${t.tenantId}"
                  data-tenant='${JSON.stringify(t).replace(/'/g, "&#39;")}'
                >${tName(t.name)}</span>
              </td>
              <td>${t.contactPerson || '—'}</td>
              <td>${t.contactEmail
                ? `<a href="mailto:${t.contactEmail}">${t.contactEmail}</a>`
                : '—'}</td>
              <td><small class="text-muted">${t.createdAt ? new Date(t.createdAt).toLocaleDateString(navigator.language) : '—'}</small></td>
              ${isPlatform ? `<td>${addonCellHTML(t)}</td>` : ''}
            </tr>
          `).join('');

      // Click on name → edit modal
      tbody.querySelectorAll('.tenant-name-link').forEach(el => {
        el.addEventListener('click', () => {
          const tenant = JSON.parse(el.dataset.tenant);
          openTenantModal('edit', tenant, async (updates) => {
            await updateTenant(tenant.tenantId, updates);
            window.showToast('Tenant updated', 'success');
            await renderTenants();
          });
        });
      });

      // Manage the data-export add-on (platform only).
      tbody.querySelectorAll('.btn-addon').forEach(btn => {
        btn.addEventListener('click', () => {
          const t = tenants.find(x => x.tenantId === btn.dataset.tenantId);
          openAddonModal(t, () => renderTenants());
        });
      });

    } catch (err) {
      console.error(err);
      tbody.innerHTML = `<tr><td colspan="${colspan}" class="text-center text-danger py-4">Failed to load tenants</td></tr>`;
      window.showToast('Failed to load tenants', 'danger');
    } finally {
      window.hidePlaneSpinner();
    }
  }

  // ── Create Button (only present for platform-level roles) ─────────────────
  document.getElementById('btn-new-tenant')?.addEventListener('click', () => {
    openTenantModal('create', null, async (data) => {
      await createTenant(data);
      window.showToast(`Tenant "${data.name}" created`, 'success');
      await renderTenants();
    });
  });

  await renderTenants();
}

// ── Modal HTML (Create / Edit) ────────────────────────────────────────────
function tenantModalHTML(mode) {
  const id = `tenantModal-${mode}`;
  const title = mode === 'create' ? 'Create New Tenant' : 'Edit Tenant';
  return `
    <div class="modal fade" id="${id}" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content bg-dark text-white">
          <div class="modal-header">
            <h5 class="modal-title"><i class="fa-solid fa-building me-2"></i>${title}</h5>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label">Tenant Name <span class="text-danger">*</span></label>
              <input id="${id}-name" type="text" class="form-control bg-dark text-white tenant-blur" placeholder="Company name">
            </div>
            <div class="mb-3">
              <label class="form-label">Contact Person</label>
              <input id="${id}-contactPerson" type="text" class="form-control bg-dark text-white" placeholder="First and last name">
            </div>
            <div class="mb-3">
              <label class="form-label">Contact Email</label>
              <input id="${id}-contactEmail" type="email" class="form-control bg-dark text-white" placeholder="contact@company.com">
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
            <button id="${id}-save" type="button" class="btn btn-success">
              <i class="fa-solid fa-check me-1"></i>${mode === 'create' ? 'Create' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>`;
}

// ── Open modal + save logic ───────────────────────────────────────────────
function openTenantModal(mode, prefill, onSave) {
  const id = `tenantModal-${mode}`;
  const modalEl = document.getElementById(id);
  if (!modalEl) return;

  modalEl.querySelector(`#${id}-name`).value         = prefill?.name          || '';
  modalEl.querySelector(`#${id}-contactPerson`).value = prefill?.contactPerson || '';
  modalEl.querySelector(`#${id}-contactEmail`).value  = prefill?.contactEmail  || '';

  const modal = bootstrap.Modal.getOrCreateInstance(modalEl);
  modal.show();

  const saveBtn = modalEl.querySelector(`#${id}-save`);
  const newSaveBtn = saveBtn.cloneNode(true);
  saveBtn.replaceWith(newSaveBtn);

  newSaveBtn.addEventListener('click', async () => {
    const name = modalEl.querySelector(`#${id}-name`).value.trim();
    if (!name) {
      modalEl.querySelector(`#${id}-name`).focus();
      window.showToast('Tenant Name is required', 'warning');
      return;
    }

    newSaveBtn.disabled = true;
    newSaveBtn.innerHTML = `<span class="spinner-border spinner-border-sm me-2"></span>Saving...`;

    try {
      await onSave({
        name,
        contactPerson: modalEl.querySelector(`#${id}-contactPerson`).value.trim() || null,
        contactEmail:  modalEl.querySelector(`#${id}-contactEmail`).value.trim()  || null,
      });
      modal.hide();
    } catch (err) {
      console.error(err);
      window.showToast('Save failed — please try again', 'danger');
    } finally {
      newSaveBtn.disabled = false;
      newSaveBtn.innerHTML = `<i class="fa-solid fa-check me-1"></i>${mode === 'create' ? 'Create' : 'Save Changes'}`;
    }
  });
}

// ── Add-on entitlements (platform-only) ─────────────────────────────────────
// The paid modules a platform admin can grant per tenant. Add future add-ons here
// and they appear as a pill in the table + a section in the manage modal.
const PLATFORM_ADDONS = [
  { key: 'api',        label: 'API', title: 'Customer API',  icon: 'fa-code',            desc: 'Read asset data + webhooks via API keys.' },
  { key: 'dataExport', label: 'Exp', title: 'Data export',   icon: 'fa-file-arrow-down', desc: 'Report builder + CSV/XLSX/PDF export.' },
];

function isAddonActive(a) {
  return a?.enabled === true && (!a.expiresAt || new Date(a.expiresAt) > new Date());
}

// A compact pill per add-on: green when active, gray when off (with a tooltip).
function addonCellHTML(t) {
  const pills = PLATFORM_ADDONS.map(def => {
    const a = t.addons?.[def.key];
    const active = isAddonActive(a);
    const until = active && a.expiresAt ? ` until ${new Date(a.expiresAt).toLocaleDateString(navigator.language)}` : '';
    return `<span class="badge ${active ? 'badge-soft-green' : 'badge-soft-gray'} me-1" title="${def.title}: ${active ? 'on' + until : 'off'}">${def.label}</span>`;
  }).join('');
  return `${pills}<button class="btn btn-sm btn-outline-secondary ms-1 btn-addon" data-tenant-id="${t.tenantId}">Manage</button>`;
}

function addonModalHTML() {
  const section = (def) => `
    <div class="border border-secondary rounded p-3 mb-3">
      <div class="form-check form-switch mb-2">
        <input class="form-check-input addon-enabled" type="checkbox" data-addon="${def.key}" id="addon-enabled-${def.key}">
        <label class="form-check-label fw-semibold" for="addon-enabled-${def.key}"><i class="fa-solid ${def.icon} me-2"></i>${def.title}</label>
      </div>
      <p class="small text-white-50 mb-2">${def.desc}</p>
      <div class="row g-2">
        <div class="col-sm-6">
          <label class="form-label small mb-1">Expires on <span class="text-white-50">(optional)</span></label>
          <input type="date" class="form-control form-control-sm bg-dark text-white addon-expires" data-addon="${def.key}">
        </div>
        <div class="col-sm-6">
          <label class="form-label small mb-1">Contract note <span class="text-white-50">(optional)</span></label>
          <input type="text" maxlength="200" class="form-control form-control-sm bg-dark text-white addon-note" data-addon="${def.key}" placeholder="e.g. Contract SB-2026-014">
        </div>
      </div>
    </div>`;
  return `
    <div class="modal fade" id="addonModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content bg-dark text-white">
          <div class="modal-header">
            <h5 class="modal-title"><i class="fa-solid fa-cubes me-2"></i>Add-ons</h5>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <p class="small text-white-50 mb-3" id="addon-tenant-name"></p>
            ${PLATFORM_ADDONS.map(section).join('')}
            <div class="form-text text-white-50">An expiry auto-disables the add-on after that date; leave empty for a manual toggle.</div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
            <button id="addon-save" type="button" class="btn btn-success"><i class="fa-solid fa-check me-1"></i>Save</button>
          </div>
        </div>
      </div>
    </div>`;
}

function openAddonModal(tenant, onDone) {
  const el = document.getElementById('addonModal');
  if (!el || !tenant) return;
  el.querySelector('#addon-tenant-name').innerHTML = tName(tenant.name || tenant.tenantId);
  // Prefill each add-on section from the tenant record, and remember the initial
  // state so we only write the ones that actually changed.
  const initial = {};
  for (const def of PLATFORM_ADDONS) {
    const a = tenant.addons?.[def.key] || {};
    const enabled = isAddonActive(a);
    const exp = a.expiresAt ? a.expiresAt.slice(0, 10) : '';
    const note = a.note || '';
    el.querySelector(`.addon-enabled[data-addon="${def.key}"]`).checked = enabled;
    el.querySelector(`.addon-expires[data-addon="${def.key}"]`).value = exp;
    el.querySelector(`.addon-note[data-addon="${def.key}"]`).value = note;
    initial[def.key] = { enabled, exp, note };
  }

  const modal = bootstrap.Modal.getOrCreateInstance(el);
  modal.show();

  const saveBtn = el.querySelector('#addon-save');
  const fresh = saveBtn.cloneNode(true);
  saveBtn.replaceWith(fresh);
  fresh.addEventListener('click', async () => {
    // Only send add-ons whose values changed.
    const changes = [];
    for (const def of PLATFORM_ADDONS) {
      const enabled = el.querySelector(`.addon-enabled[data-addon="${def.key}"]`).checked;
      const exp = el.querySelector(`.addon-expires[data-addon="${def.key}"]`).value;
      const note = el.querySelector(`.addon-note[data-addon="${def.key}"]`).value.trim();
      const init = initial[def.key];
      if (enabled === init.enabled && exp === init.exp && note === (init.note || '')) continue;
      changes.push({
        def,
        payload: {
          addon: def.key,
          enabled,
          expiresAt: exp ? new Date(exp + 'T00:00:00Z').toISOString() : null,
          note: note || null,
        },
      });
    }
    if (!changes.length) { modal.hide(); return; }
    fresh.disabled = true;
    fresh.innerHTML = `<span class="spinner-border spinner-border-sm me-2"></span>Saving…`;
    try {
      for (const c of changes) await setTenantAddon(tenant.tenantId, c.payload);
      modal.hide();
      window.showToast(`Add-ons updated for ${tenant.name}`, 'success');
      onDone?.();
    } catch (e) {
      window.showToast(`Save failed: ${e.message}`, 'danger');
    } finally {
      fresh.disabled = false;
      fresh.innerHTML = `<i class="fa-solid fa-check me-1"></i>Save`;
    }
  });
}